import {
  DataGrid,
  type GridSortModel,
  type DataGridProps,
  type GridValidRowModel,
  type GridColDef,
} from '@mui/x-data-grid'
import { useLayout } from 'hooks/useLayout'
import { MobileDataTable } from 'components/DataTable/MobileDataTable'
import { useState } from 'react'
import { isEmpty } from 'lodash'
import { Box } from '@mui/material'
import { NoDataIndicator } from 'components/NoDataIndicator/NoDataIndicator'
import { colorPrimitives } from 'components/Theme'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'

export const DataTable = <RowModelT extends GridValidRowModel>({
  autoHeight,
  hideFooter,
  rowHeight,
  sortFieldName,
  sort,
  noDataMessage = 'No data found',
  forceDesktopTableInMobile = false,
  emptyDataImageSource,
  ...props
}: DataGridProps<RowModelT> & {
  autoHeight?: boolean
  hideFooter?: boolean
  rowHeight?: number
  sortFieldName?: string
  sort?: 'asc' | 'desc'
  noDataMessage?: string
  forceDesktopTableInMobile?: boolean
  emptyDataImageSource?: string
}) => {
  const { isMobile } = useLayout()
  const {
    columns,
    rows,
    loading,
    onRowClick,
    checkboxSelection,
    onRowSelectionModelChange,
    rowSelectionModel,
  } = props

  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: sortFieldName ?? 'name',
      sort: sort ?? 'asc',
    },
  ])

  if (loading && isEmpty(rows)) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={240}
      >
        <ActivityIndicator size="medium" />
      </Box>
    )
  }

  if (isEmpty(rows)) {
    return (
      <NoDataIndicator
        noDataMessage={noDataMessage}
        imageSource={emptyDataImageSource}
      />
    )
  }

  if (isMobile && !forceDesktopTableInMobile) {
    return (
      <MobileDataTable
        columns={columns as Array<GridColDef<RowModelT>>}
        rows={rows}
        onRowClick={onRowClick}
        checkboxSelection={checkboxSelection}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={onRowSelectionModelChange}
      />
    )
  }

  return (
    <DataGrid<RowModelT>
      // To let the grid automatically calculate the height based on the cell content
      getRowHeight={() => 'auto'}
      // Optional: Provide an estimated row height to improve scroll performance
      getEstimatedRowHeight={() => 100}
      sx={{
        '& .MuiDataGrid-row:hover': {
          backgroundColor: colorPrimitives.grayDataTableHover,
        },
        '& .MuiDataGrid-cell.reorder-drop-cell': {
          p: 0,
        },
        '& .MuiDataGrid-cell.reorder-drop-cell > div': {
          height: '100%',
        },
        '& .MuiDataGrid-columnHeader.reorder-drop-header': {
          p: 0,
        },
        '& .MuiDataGrid-columnHeaderTitleContainer .MuiDataGrid-iconButtonContainer':
          {
            display: 'none',
          },
        '& .MuiDataGrid-row, & .MuiDataGrid-cell:first-of-type': {
          color: colorPrimitives.black,
        },
      }}
      autoHeight={autoHeight ?? true}
      rowHeight={rowHeight}
      checkboxSelection={checkboxSelection}
      disableRowSelectionOnClick
      disableColumnMenu
      sortModel={sortModel}
      onSortModelChange={(model) => {
        setSortModel(model)
      }}
      initialState={{
        pagination: {
          paginationModel: { page: 0, pageSize: 25 },
        },
      }}
      hideFooter={hideFooter ?? props.rows.length <= 25}
      pageSizeOptions={[25, 50, 100]}
      {...props}
    />
  )
}
